import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { t } from 'i18next';
import { useUsers } from '../../../hooks/useUsers';
import { useGroups } from '../../../hooks/useGroups';
import {
    assignQuestionnaireToPatients,
    assignQuestionnaireToGroup
} from '../../../services/questionnaire';

const BulkAssignDialog = ({ open, onClose, questionnaireUUIDs }) => {
    const [assignMode, setAssignMode] = useState(null);
    const [selectedPatientUUIDs, setSelectedPatientUUIDs] = useState([]);
    const [selectedGroupUUIDs, setSelectedGroupUUIDs] = useState([]);
    const [notBefore, setNotBefore] = useState(dayjs().format('YYYY-MM-DDTHH:mm'));

    const [assignedTo, setAssignedTo] = useState(null);
    const [assignedEntities, setAssignedEntities] = useState([]);

    const [selectedPatients, setSelectedPatients] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);

    const {
        data: patientRows = [],
        count,
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions
    } = useUsers(['PATIENT']);

    const {
        data: groupRows = [],
        count: groupCount,
        rowsPerPageOptions: groupPageSizeOptions,
        paginationModel: groupPagination,
        setPaginationModel: setGroupPagination
    } = useGroups();

    const handleAssign = async () => {
        try {
            if (assignMode === 'patients') {
                for (const qid of questionnaireUUIDs) {
                    await assignQuestionnaireToPatients(
                        qid,
                        selectedPatientUUIDs,
                        notBefore
                    );
                }

                setAssignedTo('patients');
                setAssignedEntities(selectedPatients);
            } else if (assignMode === 'group') {
                for (const qid of questionnaireUUIDs) {
                    for (const gid of selectedGroupUUIDs) {
                        await assignQuestionnaireToGroup(qid, gid, notBefore);
                    }
                }

                setAssignedTo('group');
                setAssignedEntities(
                    selectedGroups.map((g) => ({
                        name: t('questionnaires.assigned_group_prefix', { name: g.name })
                    }))
                );
            }

            setAssignMode(null);
            setSelectedPatientUUIDs([]);
            setSelectedGroupUUIDs([]);
            setSelectedPatients([]);
            setSelectedGroups([]);
        } catch (err) {
            console.error('Erro ao atribuir em massa:', err);
        }
    };

    const resetDialogState = () => {
        setAssignMode(null);
        setSelectedPatientUUIDs([]);
        setSelectedGroupUUIDs([]);
        setSelectedPatients([]);
        setSelectedGroups([]);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('questionnaires.assign_dialog_title')}</DialogTitle>

            <DialogContent>
                {assignedTo && assignedEntities.length > 0 ? (
                    <Box
                        sx={{
                            backgroundColor: '#e6f4ea',
                            border: '1px solid #4caf50',
                            borderRadius: 2,
                            p: 2,
                            mt: 2
                        }}
                    >
                        <Typography variant="h6" color="green" gutterBottom>
                            {t('questionnaires.assign_success', {
                                type:
                                    assignedTo === 'patients'
                                        ? t('questionnaires.patients')
                                        : t('questionnaires.group')
                            })}
                        </Typography>

                        <ul>
                            {assignedEntities.map((entity, idx) => (
                                <li key={idx}>
                                    <Typography variant="body1">{entity.name}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Box>
                ) : (
                    <>
                        {!assignMode && (
                            <Box display="flex" gap={2}>
                                <Button
                                    variant="contained"
                                    onClick={() => setAssignMode('patients')}
                                >
                                    {t('questionnaires.assign_to_patients')}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => setAssignMode('group')}
                                >
                                    {t('questionnaires.assign_to_group')}
                                </Button>
                            </Box>
                        )}

                        {assignMode === 'patients' && (
                            <>
                                <TextField
                                    type="datetime-local"
                                    label={t('questionnaires.available_from')}
                                    value={notBefore}
                                    onChange={(e) => setNotBefore(e.target.value)}
                                    sx={{ mt: 2, mb: 2 }}
                                    InputLabelProps={{ shrink: true }}
                                />

                                <DataGrid
                                    autoHeight
                                    checkboxSelection
                                    disableRowSelectionOnClick
                                    rows={patientRows}
                                    getRowId={(row) => row.role_uuid}
                                    columns={[
                                        {
                                            field: 'name',
                                            headerName: t('questionnaires.columns.name'),
                                            flex: 1
                                        }
                                    ]}
                                    rowSelectionModel={selectedPatientUUIDs.filter((id) =>
                                        patientRows.some((p) => p.role_uuid === id)
                                    )}
                                    onRowSelectionModelChange={(newSelection) => {
                                        setSelectedPatientUUIDs((prevUUIDs) => {
                                            const uuidSet = new Set(prevUUIDs);
                                            newSelection.forEach((id) => uuidSet.add(id));

                                            const currentPageIds = patientRows.map(
                                                (p) => p.role_uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    uuidSet.delete(id);
                                            });

                                            return Array.from(uuidSet);
                                        });

                                        setSelectedPatients((prev) => {
                                            const map = new Map(
                                                prev.map((p) => [p.uuid, p])
                                            );

                                            newSelection.forEach((id) => {
                                                const row = patientRows.find(
                                                    (p) => p.role_uuid === id
                                                );
                                                if (row)
                                                    map.set(id, {
                                                        uuid: id,
                                                        name: row.name
                                                    });
                                            });

                                            const currentPageIds = patientRows.map(
                                                (p) => p.role_uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    map.delete(id);
                                            });

                                            return Array.from(map.values());
                                        });
                                    }}
                                    paginationMode="server"
                                    rowCount={count}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    pageSizeOptions={rowsPerPageOptions}
                                />
                            </>
                        )}

                        {assignMode === 'group' && (
                            <>
                                <TextField
                                    type="datetime-local"
                                    label={t('questionnaires.available_from')}
                                    value={notBefore}
                                    onChange={(e) => setNotBefore(e.target.value)}
                                    sx={{ mt: 2, mb: 2 }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <DataGrid
                                    autoHeight
                                    checkboxSelection
                                    disableRowSelectionOnClick
                                    rows={groupRows}
                                    getRowId={(row) => row.uuid}
                                    columns={[
                                        {
                                            field: 'name',
                                            headerName: t('questionnaires.columns.name'),
                                            flex: 1
                                        },
                                        {
                                            field: 'description',
                                            headerName: t('groups.description_label'),
                                            flex: 2
                                        }
                                    ]}
                                    rowSelectionModel={selectedGroupUUIDs.filter((id) =>
                                        groupRows.some((g) => g.uuid === id)
                                    )}
                                    onRowSelectionModelChange={(newSelection) => {
                                        setSelectedGroupUUIDs((prevUUIDs) => {
                                            const uuidSet = new Set(prevUUIDs);

                                            newSelection.forEach((id) => uuidSet.add(id));

                                            const currentPageIds = groupRows.map(
                                                (g) => g.uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    uuidSet.delete(id);
                                            });

                                            return Array.from(uuidSet);
                                        });

                                        setSelectedGroups((prev) => {
                                            const map = new Map(
                                                prev.map((g) => [g.uuid, g])
                                            );

                                            newSelection.forEach((id) => {
                                                const row = groupRows.find(
                                                    (g) => g.uuid === id
                                                );
                                                if (row)
                                                    map.set(id, {
                                                        uuid: id,
                                                        name: row.name
                                                    });
                                            });

                                            const currentPageIds = groupRows.map(
                                                (g) => g.uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    map.delete(id);
                                            });

                                            return Array.from(map.values());
                                        });
                                    }}
                                    paginationMode="server"
                                    rowCount={groupCount}
                                    paginationModel={groupPagination}
                                    onPaginationModelChange={setGroupPagination}
                                    pageSizeOptions={groupPageSizeOptions}
                                />
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Button
                        onClick={() => {
                            resetDialogState();
                            onClose();
                        }}
                    >
                        {assignedTo && assignedEntities.length > 0
                            ? t('common.ok')
                            : t('questionnaires.cancel')}
                    </Button>
                </Box>

                <Box display="flex" gap={1}>
                    {assignedTo && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setAssignedTo(null);
                                setAssignedEntities([]);
                                setSelectedPatientUUIDs([]);
                                setSelectedGroupUUIDs([]);
                                setAssignMode(null);
                            }}
                        >
                            {t('questionnaires.assign_again')}
                        </Button>
                    )}

                    {assignMode && (
                        <Button
                            variant="contained"
                            onClick={handleAssign}
                            disabled={
                                (assignMode === 'patients' &&
                                    selectedPatientUUIDs.length === 0) ||
                                (assignMode === 'group' &&
                                    selectedGroupUUIDs.length === 0)
                            }
                        >
                            {t('questionnaires.confirm_assignment')}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAssignDialog;
